import "./PromoInsightBlock.scss";
import cn from "classnames";
import {
  ArrowLineUpRightIcon,
  ArrowRightIcon,
  Button,
  Link,
  OptimizelyEditProperty,
  Picture,
} from "/App.Components";
import { ColorTheme } from "/Models/Models/ColorTheme";
import { PromoInsightBlockProps } from "/Models/Generated/PromoInsightBlockProps";
import { PromoInsightItemBlockProps } from "/Models/Generated/PromoInsightItemBlockProps";
import { PromoInsightVariant } from "/Models/Generated/PromoInsightVariant";

const PromoInsightBlock = ({
  heading,
  headingPropertyName,
  description,
  descriptionPropertyName,
  primaryLink,
  primaryLinkPropertyName,
  secondaryLink,
  secondaryLinkPropertyName,
  variant,
  items,
  itemsPropertyName,
}: PromoInsightBlockProps) => {
  const visibleItems = items?.slice(0, 3) ?? [];
  const useImages = variant === PromoInsightVariant.Images;

  return (
    <section
      className={cn("promo-insight-block-norad", {
        "promo-insight-block-norad--images": useImages,
        "promo-insight-block-norad--figures": !useImages,
      })}
    >
      <div className="promo-insight-block-norad__content">
        <div className="promo-insight-block-norad__copy">
          <OptimizelyEditProperty propertyName={headingPropertyName}>
            {heading && (
              <h2 className="promo-insight-block-norad__heading">{heading}</h2>
            )}
          </OptimizelyEditProperty>
          <OptimizelyEditProperty propertyName={descriptionPropertyName}>
            {description && (
              <p className="promo-insight-block-norad__description">
                {description}
              </p>
            )}
          </OptimizelyEditProperty>
        </div>

        <div className="promo-insight-block-norad__actions">
          <OptimizelyEditProperty
            propertyName={primaryLinkPropertyName}
            clientRender
          >
            {primaryLink?.href && primaryLink.text && (
              <Button
                style="primary"
                size="m"
                href={primaryLink.href}
                target={primaryLink.target}
                label={primaryLink.text}
              />
            )}
          </OptimizelyEditProperty>
          <OptimizelyEditProperty
            propertyName={secondaryLinkPropertyName}
            clientRender
          >
            {secondaryLink?.href && secondaryLink.text && (
              <Button
                style="primary"
                size="m"
                href={secondaryLink.href}
                target={secondaryLink.target}
                label={secondaryLink.text}
              />
            )}
          </OptimizelyEditProperty>
        </div>
      </div>

      <div className="promo-insight-block-norad__items-container">
        <OptimizelyEditProperty propertyName={itemsPropertyName} clientRender>
          {visibleItems.length > 0 && (
            <ul
              className={cn("promo-insight-block-norad__items", {
                "promo-insight-block-norad__items--two":
                  visibleItems.length === 2,
                "promo-insight-block-norad__items--three":
                  visibleItems.length === 3,
              })}
            >
              {visibleItems.map((item, index) => (
                <PromoInsightItem
                  key={`${item.heading ?? "item"}-${index}`}
                  item={item}
                  useImages={useImages}
                />
              ))}
            </ul>
          )}
        </OptimizelyEditProperty>
      </div>
    </section>
  );
};

interface PromoInsightItemProps {
  item: PromoInsightItemBlockProps;
  useImages: boolean;
}

const PromoInsightItem = ({ item, useImages }: PromoInsightItemProps) => (
  <li
    className={cn("promo-insight-block-norad__item", {
      "promo-insight-block-norad__item--image": useImages,
      "promo-insight-block-norad__item--figure": !useImages,
    })}
  >
    {useImages ? (
      <>
        {item.image && (
          <div className="promo-insight-block-norad__image">
            <Picture {...item.image} pictureBehaviour="Cover" />
          </div>
        )}
        {item.heading && (
          <h3 className="promo-insight-block-norad__item-heading">
            {item.heading}
          </h3>
        )}
      </>
    ) : (
      <div className="promo-insight-block-norad__figure-wrapper">
        {item.figure && (
          <p className="promo-insight-block-norad__figure">
            {item.figure}
          </p>
        )}
        {item.heading && (
          <p className="promo-insight-block-norad__figure-heading">
            {item.heading}
          </p>
        )}
      </div>
    )}

    <div className="promo-insight-block-norad__item-content">
      {item.description && (
        <p className="promo-insight-block-norad__item-description">
          {item.description}
        </p>
      )}
      {item.readMoreLink && (
        <div className="promo-insight-block-norad__item-link">
          <Link
            {...item.readMoreLink}
            target={
              item.readMoreLink.isExternal ? "_blank" : item.readMoreLink.target
            }
            icon={
              item.readMoreLink.isExternal
                ? ArrowLineUpRightIcon
                : ArrowRightIcon
            }
            iconPlacement="after"
            iconSize="20"
            iconThickness="Reg"
            colorTheme={ColorTheme.default}
            size="x-small"
          />
        </div>
      )}
    </div>
  </li>
);

export default PromoInsightBlock;
